// ──────────────────────────────────────────────────────────────────────────
// Staff pay calculation (period-based).
//
// Hours come from the staff member's resolved working window per day. For v1
// that's the ROTA baseline (rota = paid as normal for leave days, which is the
// chosen policy). `buildBreakdown` accepts a per-day `overrides` map so the
// diary layer (actual shifts incl. extend/close + overrun flags) can refine it
// later without changing the calc.
//
// Pay basis:
//   Hourly  → total worked hours × rate
//   Daily   → days worked × rate
//   Weekly  → salary spread across that week's scheduled rota days
//   Monthly → salary spread across that month's scheduled rota days
// ──────────────────────────────────────────────────────────────────────────

export const PAY_BASES = ["Hourly", "Daily", "Weekly", "Monthly"];

// JS getDay() is 0=Sun..6=Sat; rota profiles use these keys.
const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Working minutes for a date per the rota baseline (0 if off, or before the
// rota's start date).
export function rotaMinutesForDate(rotaInfo, date) {
  const profiles = rotaInfo && rotaInfo.rota && rotaInfo.rota.schedule
    ? rotaInfo.rota.schedule[0] && rotaInfo.rota.schedule[0].profiles
    : null;
  if (!profiles) return 0;

  if (rotaInfo.rota.startDate) {
    const start = startOfDay(new Date(rotaInfo.rota.startDate));
    if (startOfDay(date) < start) return 0;
  }

  const dayKey = DAY_KEYS[date.getDay()];
  const profile = profiles.find((p) => p.day === dayKey);
  if (!profile || profile.startTime == null || profile.endTime == null) return 0;

  let mins = profile.endTime - profile.startTime;
  (profile.breakTimes || []).forEach((b) => {
    if (b && b.startTime != null && b.endTime != null) {
      mins -= Math.max(0, b.endTime - b.startTime);
    }
  });
  return Math.max(0, mins);
}

// Per-day breakdown over the inclusive [startDate, endDate] range.
// overrides[ymd] may supply { minutes, isLeave, leaveType, overrunMins } from
// the diary (actual shift data) to override the rota baseline for that day.
export function buildBreakdown(rotaInfo, startDate, endDate, overrides = {}) {
  const days = [];
  let d = startOfDay(startDate);
  const end = startOfDay(endDate);
  while (d <= end) {
    const key = ymd(d);
    const ov = overrides[key];
    const rotaMins = rotaMinutesForDate(rotaInfo, d);
    const minutes = ov && ov.minutes != null ? ov.minutes : rotaMins;
    days.push({
      date: new Date(d),
      key,
      minutes,
      rotaMinutes: rotaMins,
      isLeave: ov ? !!ov.isLeave : false,
      leaveType: ov ? ov.leaveType : null,
      overrunMins: ov ? ov.overrunMins || 0 : 0,
      source: ov && ov.minutes != null ? "shift" : "rota",
    });
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  }
  return days;
}

// ── Salary distribution helpers ──
// A weekly/monthly salary is spread across that period's own scheduled rota
// days, so the daily rate self-adjusts per period (e.g. a 28-day February and a
// 31-day March both pay the full monthly salary). This is computed entirely
// from the rota — no extra backend.

function weekBounds(date) {
  const s = startOfWeekMon(date);
  const e = new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
  return [s, e];
}
function monthBounds(date) {
  const s = new Date(date.getFullYear(), date.getMonth(), 1);
  const e = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return [s, e];
}

// Count scheduled rota days (days with working hours) in an inclusive range.
export function countRotaDaysInRange(rotaInfo, start, end) {
  let count = 0;
  let d = startOfDay(start);
  const last = startOfDay(end);
  while (d <= last) {
    if (rotaMinutesForDate(rotaInfo, d) > 0) count++;
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  }
  return count;
}

// Does this staff member have any working pattern at all? Salaried pay can't be
// derived without one (it's the divisor), so we guard rather than show a wrong
// figure.
export function hasAnyRota(rotaInfo) {
  const profiles =
    rotaInfo && rotaInfo.rota && rotaInfo.rota.schedule
      ? rotaInfo.rota.schedule[0] && rotaInfo.rota.schedule[0].profiles
      : null;
  if (!profiles) return false;
  return profiles.some(
    (p) => p && p.startTime != null && p.endTime != null && p.endTime > p.startTime
  );
}

export function computePay({ payBasis, payRate, days, rotaInfo }) {
  const rate = parseFloat(payRate) || 0;
  const totalMinutes = days.reduce((s, x) => s + (x.minutes || 0), 0);
  const totalHours = totalMinutes / 60;
  const daysWorked = days.filter((x) => (x.minutes || 0) > 0).length;
  const leaveDays = days.filter((x) => x.isLeave).length;
  const overrunMins = days.reduce((s, x) => s + (x.overrunMins || 0), 0);
  const overrunDays = days.filter((x) => (x.overrunMins || 0) > 0).length;
  const rangeDays = days.length;

  let pay = 0;
  let needsRota = false;

  switch (payBasis) {
    case "Hourly":
      pay = totalHours * rate;
      break;
    case "Daily":
      pay = daysWorked * rate;
      break;
    case "Weekly":
    case "Monthly": {
      // No rota = no working pattern to divide the salary by.
      if (!hasAnyRota(rotaInfo)) {
        needsRota = true;
        break;
      }
      // Each scheduled day earns salary ÷ (rota days in that day's period), so
      // a full week/month sums back to exactly the salary.
      const periodOf = payBasis === "Weekly" ? weekBounds : monthBounds;
      const denomCache = {};
      days.forEach((d) => {
        if ((d.minutes || 0) <= 0) return; // only scheduled rota days earn
        const [pStart, pEnd] = periodOf(d.date);
        const key = ymd(pStart);
        if (denomCache[key] == null) {
          denomCache[key] = countRotaDaysInRange(rotaInfo, pStart, pEnd);
        }
        const denom = denomCache[key];
        if (denom > 0) pay += rate / denom;
      });
      break;
    }
    default:
      pay = 0;
  }

  const dailyRate = daysWorked > 0 ? pay / daysWorked : 0;

  return {
    pay, totalHours, totalMinutes, daysWorked, leaveDays,
    overrunMins, overrunDays, rangeDays, dailyRate, needsRota,
  };
}

// A plain-English breakdown of how `pay` was derived, so the figure is never a
// black box. For salaried bases the "rate" shown is the salary, joined with "·"
// (across) rather than "×" (multiply), since the per-day maths is the period
// distribution, not a flat multiplication.
export function explainPay(basis, calc) {
  const dayStr = `${calc.daysWorked} day${calc.daysWorked === 1 ? "" : "s"}`;
  switch (basis) {
    case "Hourly":
      return { factorValue: `${round2(calc.totalHours)} hrs`, sep: "×", rateLabel: "/ hr" };
    case "Daily":
      return { factorValue: dayStr, sep: "×", rateLabel: "/ day" };
    case "Weekly":
      return { factorValue: dayStr, sep: "·", rateLabel: "/ wk" };
    case "Monthly":
      return { factorValue: dayStr, sep: "·", rateLabel: "/ mo" };
    default:
      return { factorValue: "", sep: "", rateLabel: "" };
  }
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function startOfWeekMon(d) {
  const x = startOfDay(d);
  const dow = (x.getDay() + 6) % 7; // Mon = 0 … Sun = 6
  return new Date(x.getFullYear(), x.getMonth(), x.getDate() - dow);
}

// Adaptive breakdown: a flat daily list reads fine for short ranges but becomes
// an endless scroll over a quarter. Past a threshold we roll up into weeks
// (each expandable to its days), so the breakdown stays scannable at any range.
export function groupBreakdown(days, dayThreshold = 16) {
  const active = days.filter((d) => (d.minutes || 0) > 0 || d.isLeave);
  if (days.length <= dayThreshold) {
    return { mode: "day", rows: active };
  }
  const map = new Map();
  active.forEach((d) => {
    const ws = startOfWeekMon(d.date);
    const key = ymd(ws);
    if (!map.has(key)) map.set(key, { key, weekStart: ws, days: [], minutes: 0 });
    const g = map.get(key);
    g.days.push(d);
    g.minutes += d.minutes || 0;
  });
  const rows = [...map.values()]
    .sort((a, b) => a.weekStart - b.weekStart)
    .map((g) => {
      const weekEnd = new Date(g.weekStart.getFullYear(), g.weekStart.getMonth(), g.weekStart.getDate() + 6);
      return {
        ...g,
        weekEnd,
        daysWorked: g.days.filter((d) => (d.minutes || 0) > 0).length,
        leaveDays: g.days.filter((d) => d.isLeave).length,
      };
    });
  const maxMinutes = rows.reduce((m, r) => Math.max(m, r.minutes), 0);
  return { mode: "week", rows, maxMinutes };
}

export function formatHours(hours) {
  if (!hours) return "0h";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export function formatMoney(amount, symbol = "£") {
  const n = Number(amount) || 0;
  return `${symbol}${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// How the rate is expressed, for the "/ x" suffix next to the amount.
export function rateSuffix(basis) {
  switch (basis) {
    case "Hourly": return "/ hr";
    case "Daily": return "/ day";
    case "Weekly": return "/ wk";
    case "Monthly": return "/ mo";
    default: return "";
  }
}
